import { type App } from 'vue'
import { type Pinia } from 'pinia'
import { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { Notify } from 'quasar'
import { $t } from 'src/boot/common/i18n'
import { useErrorStore } from 'stores/common/error'
import { useErrorLog } from 'composables/common/error/useErrorLog'

export const useAuthInterceptor = () => {
  /**
   * Adds the locale to the Axios request config's baseURL based on the app's current locale.
   * @param config - The Axios request config.
   * @param app - The Vue app instance.
   * @returns The updated Axios request config.
   */
  const addLocale = (config: InternalAxiosRequestConfig, app: App) => {
    const locale = app.config.globalProperties.$i18n
      ? app.config.globalProperties.$i18n.locale.split('-')
      : ['en']

    if (config.baseURL === process.env.API_BASE_URL) {
      config.baseURL += `/${locale[0]}`
    }

    return config
  }

  /**
   * Interceptor function for handling responses from Axios requests.
   * Sets the CTA (Call to Action) if any product data is present in the response.
   *
   * @param response - The Axios response object.
   * @param store - The Pinia store object.
   * @returns The modified Axios response object.
   */
  const responseInterceptor = (response: AxiosResponse, store: Pinia) => {
    // Set the CTA if any
    if (response.data.product) {
      const ctaType = response.config.ctaType
      useErrorStore(store).callToAction[ctaType || 'generalError'] =
        response.data.product
    }
    return response
  }

  /**
   * Handles the response error from an HTTP request.
   * @param error - The error object representing the response error.
   * @param store - Optional Pinia store instance.
   * @throws The original error object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const responseErrorInterceptor = (error: any, store?: Pinia) => {
    if (process.env.SERVER) {
      useErrorLog().errorLogging(error)
    }

    // Set the CTA if any
    if (store && error.response.data.product) {
      const ctaType = error.response.config.ctaType
      useErrorStore(store).callToAction[ctaType || 'generalError'] =
        error.response.data.product
    }

    const errorCode = error.response?.status

    if (errorCode === 500 && errorCode === 404) {
      Notify.create({
        message: $t('error.anError'),
        color: 'negative',
      })
    }

    throw error
  }

  return { addLocale, responseInterceptor, responseErrorInterceptor }
}
